import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '../api/client';
import type { PersonalContact } from '../types';

// ── Settings → My CRM Privacy: the caller's OWN personal-contacts rows ───────
//
// The self-service counterpart to CRM Admin → Privacy (useCrmAdmin.ts). Same
// firm-wide suppression SEMANTIC — anyone added is hidden for every user, at
// ingest — but scoped by the `added_by` attribution stamp so each user sees +
// manages only the rows THEY added. Targets the /me/personal-contacts routes
// (plain-authenticated, NOT require_crm_privileged) so every user can reach it
// from the Settings tab. See backend crm.py §"Settings-tab self-service".

export interface MyPersonalContactFilters {
  search?: string;
  page?: number;
  pageSize?: number;
}

// /me GET returns only the caller's own on_list rows — no segment axis, and
// (unlike the admin surface) no `counts` block. Mirrors personal_screen.
// list_my_personal_contacts.
export interface MyPersonalContactsResponse {
  rows: PersonalContact[];
  total: number;
  page: number;
  page_size: number;
}

export function useMyPersonalContacts(filters: MyPersonalContactFilters = {}) {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.page != null) params.set('page', String(filters.page));
  if (filters.pageSize != null) params.set('page_size', String(filters.pageSize));
  const qs = params.toString();
  return useQuery<MyPersonalContactsResponse>({
    queryKey: ['crm-me', 'personal-contacts', filters],
    queryFn: () =>
      apiFetch<MyPersonalContactsResponse>(
        `/api/crm/me/personal-contacts${qs ? `?${qs}` : ''}`,
      ),
    staleTime: 15 * 1000,
  });
}

// POST echoes the takeover-guard outcome: already_suppressed=True means the
// address was already actively hidden and ownership was NOT re-stamped; owned
// tells the caller whether the (possibly pre-existing) row is now theirs.
// NOTE: the /me responses deliberately carry NO firm-wide interaction counts —
// the all-users route must not be an interaction-volume oracle (backend review
// finding #2). Confirmations are non-numeric.
export interface AddMyPersonalContactResult {
  id: number;
  email: string;
  already_suppressed: boolean;
  owned: boolean;
}

export function useAddMyPersonalContact() {
  const qc = useQueryClient();
  return useMutation<AddMyPersonalContactResult, Error, { email: string }>({
    mutationFn: ({ email }) =>
      apiFetch<AddMyPersonalContactResult>('/api/crm/me/personal-contacts', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm-me', 'personal-contacts'] }),
  });
}

export interface RemoveMyPersonalContactResult {
  id: number;
  email: string;
  cleared: boolean;
}

export function useRemoveMyPersonalContact() {
  const qc = useQueryClient();
  return useMutation<RemoveMyPersonalContactResult, Error, PersonalContact>({
    mutationFn: (contact) =>
      apiFetch<RemoveMyPersonalContactResult>(
        `/api/crm/me/personal-contacts/${contact.id}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm-me', 'personal-contacts'] }),
  });
}
